import React from "react";
import { Modal, Button, Card, Collapse, Form, Spinner } from "react-bootstrap";
import { FiChevronDown } from "react-icons/fi";

interface ProjectDataModalProps {
  show: boolean;
  onHide: () => void;
  onSave: () => void;
  projectData: any;
  rules: any;
  noOfHours: string;
  totals: { totalScore: number; totalDays: number };
  estimatedEndDate: string;
  expandedPhases: Set<number>;
  expandedParts: Record<string, Set<number>>;
  taskDays: Record<string, number>;
  onTogglePhase: (phaseIndex: number) => void;
  onTogglePart: (phaseIndex: number, partIndex: number) => void;
  onTaskDaysChange: (phaseIndex: number, partIndex: number, taskIndex: number, value: string) => void;
  getTimeForSubtask: (subtask: any, rules: any) => number;
  getScoreForSubtask: (subtask: any, rules: any) => number;
  calculateDays: (timeInMinutes: number, noOfHours: string) => number;
  getTaskDays: (phaseIndex: number, partIndex: number, taskIndex: number, calculatedDays: number) => number;
  saving: boolean;
}

const ProjectDataModal: React.FC<ProjectDataModalProps> = ({
  show,
  onHide,
  onSave,
  projectData,
  rules,
  noOfHours,
  totals,
  estimatedEndDate,
  expandedPhases,
  expandedParts,
  taskDays,
  onTogglePhase,
  onTogglePart,
  onTaskDaysChange,
  getTimeForSubtask,
  getScoreForSubtask,
  calculateDays,
  getTaskDays,
  saving
}) => {
  const isPhaseExpanded = (phaseIndex: number) => expandedPhases.has(phaseIndex);
  const isPartExpanded = (phaseIndex: number, partIndex: number) => {
    const key = `${phaseIndex}`;
    return expandedParts[key]?.has(partIndex) || false;
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Project Data Mapping</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {projectData && (
          <>
            <div className="d-flex justify-content-end gap-3 mb-3">
              <div className="bg-primary text-white px-3 py-2 rounded">
                Total Project Score: {totals.totalScore}
              </div>
              <div className="bg-primary text-white px-3 py-2 rounded">
                Total Project Duration: {totals.totalDays} Days
              </div>
              {estimatedEndDate && (
                <div className="bg-primary text-white px-3 py-2 rounded">
                  Estimated End Date: {estimatedEndDate}
                </div>
              )}
            </div>

            {projectData.content?.map((phase: any, phaseIndex: number) => (
              <Card key={phaseIndex} className="mb-3">
                <Card.Header
                  style={{ cursor: "pointer" }}
                  onClick={() => onTogglePhase(phaseIndex)}
                >
                  <div className="d-flex align-items-center">
                    <FiChevronDown
                      style={{
                        transform: isPhaseExpanded(phaseIndex) ? "rotate(0deg)" : "rotate(-90deg)",
                        transition: "transform 0.2s",
                        marginRight: "8px"
                      }}
                    />
                    <strong>Phase {phaseIndex + 1}: {phase.phase_name}</strong>
                  </div>
                </Card.Header>
                <Collapse in={isPhaseExpanded(phaseIndex)}>
                  <Card.Body>
                    {phase.parts?.map((part: any, partIndex: number) => (
                      <div key={partIndex} className="mb-3">
                        <div
                          style={{ cursor: "pointer", padding: "8px", backgroundColor: "#f9f9f9" }}
                          onClick={() => onTogglePart(phaseIndex, partIndex)}
                        >
                          <div className="d-flex align-items-center">
                            <FiChevronDown
                              style={{
                                transform: isPartExpanded(phaseIndex, partIndex) ? "rotate(0deg)" : "rotate(-90deg)",
                                transition: "transform 0.2s",
                                marginRight: "8px"
                              }}
                            />
                            <strong>Part {partIndex + 1}: {part.part_name}</strong>
                          </div>
                        </div>
                        <Collapse in={isPartExpanded(phaseIndex, partIndex)}>
                          <div className="ms-4 mt-2">
                            {part.tasks?.map((task: any, taskIndex: number) => {
                              let taskTotalTime = 0;
                              let taskTotalScore = 0;
                              
                              task.data.forEach((subtask: any) => {
                                taskTotalTime += getTimeForSubtask(subtask, rules);
                                taskTotalScore += getScoreForSubtask(subtask, rules);
                              });

                              const calculatedTaskDays = calculateDays(taskTotalTime, noOfHours);
                              const currentTaskDays = getTaskDays(phaseIndex, partIndex, taskIndex, calculatedTaskDays);

                              return (
                                <div key={taskIndex} className="mb-3 p-3 border rounded">
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <strong>Task {taskIndex + 1}: {task.task_name}</strong>
                                    <div className="d-flex align-items-center gap-2">
                                      <label className="mb-0">Days:</label>
                                      <Form.Control
                                        type="number"
                                        min="0.01"
                                        step="0.01"
                                        value={typeof currentTaskDays === 'number' ? currentTaskDays.toFixed(2) : currentTaskDays}
                                        onChange={(e) => onTaskDaysChange(phaseIndex, partIndex, taskIndex, e.target.value)}
                                        style={{ width: "80px" }}
                                      />
                                    </div>
                                  </div>
                                  <div className="mt-2">
                                    <ul className="mb-2">
                                      {task.data.map((subtask: any, subtaskIndex: number) => {
                                        const subtaskTime = getTimeForSubtask(subtask, rules);
                                        return (
                                          <li key={subtaskIndex} className="mb-1">
                                            <strong>{subtask.type}</strong>
                                            {subtask.type === "video" && `: ${subtask.time || 0}mins`}
                                            {subtask.type === "notes" && `: ${subtask.time || 0}mins`}
                                            {subtask.type === "project_coding" && `: ${subtaskTime}mins`}
                                            {subtask.type === "mcq" && `: ${subtaskTime}mins`}
                                            {subtask.type === "coding" && `: ${subtaskTime}mins`}
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </Collapse>
                      </div>
                    ))}
                  </Card.Body>
                </Collapse>
              </Card>
            ))}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onSave} disabled={saving}>
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProjectDataModal;

